import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
    Dialog,
    NotificationService,
    SelectionManager,
} from '@vendure/admin-ui/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    switchMap,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ShoppingListService } from '../../shopping-list.service';

@Component({
  standalone: false,
    selector: 'vdr-assign-products-to-shopping-list-dialog',
    templateUrl: './assign-products-to-shopping-list-dialog.component.html',
    styleUrls: ['./assign-products-to-shopping-list-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignProductsToShoppingListDialogComponent
    implements OnInit, Dialog<any>
{
    items$: Observable<any[]>;
    totalItems$ = new BehaviorSubject<number>(0);
    currentPage$ = new BehaviorSubject<number>(1);
    itemsPerPage$ = new BehaviorSubject<number>(10);

    searchTermControl = new FormControl('');
    selectionManager: SelectionManager<any>;

    resolveWith: (result?: any) => void;

    // assigned by ModalService.fromComponent() call
    productVariantsIds: string[];

    constructor(
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private shoppingListService: ShoppingListService,
        private notificationService: NotificationService
    ) {
        this.selectionManager = new SelectionManager({
            multiSelect: true,
            additiveMode: true,
            itemsAreEqual: (a, b) => a.id === b.id,
        });

        this.items$ = this.shoppingListService.shoppingLists$;
    }

    ngOnInit() {
        this.loadShoppingLists();
        this.initializeSearchSubscription();

        this.totalItems$ = new BehaviorSubject<number>(0);
        this.shoppingListService.totalItemsShoppingList$.subscribe((total) => {
            (this.totalItems$ as BehaviorSubject<number>).next(total);
        });
    }

    loadShoppingLists(skip: number = 0, take: number = 10): void {
        const searchTerm = '';
        this.shoppingListService
            .searchShoppingLists(searchTerm, skip, take)
            .subscribe({
                next: (data) => {
                    this.items$ = of(data.items);
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Erro ao carregar listas de compras:', error);
                },
            });
    }

    private initializeSearchSubscription(): void {
        this.searchTermControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                map((term) => term?.trim() || ''),
                switchMap((term) =>
                    this.shoppingListService.searchShoppingLists(
                        term,
                        0,
                        this.itemsPerPage$.getValue()
                    )
                )
            )
            .subscribe({
                next: (data) => {
                    this.items$ = of(data.items);
                    this.totalItems$.next(data.totalItems);
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Erro ao buscar lista de compras:', error);
                },
            });
    }

    toggleSelection(shoppingList: any) {
        this.selectionManager.toggleSelection(shoppingList);
    }

    async assign(): Promise<void> {
        const productVariantsIds = this.productVariantsIds;
        const selectedShoppingLists = this.selectionManager.selection;

        if (!selectedShoppingLists.length) {
            this.notificationService.error(
                'shopping-list.actions.no-items-selected'
            );
            return;
        }

        const shoppingListIds = selectedShoppingLists.map(
            (shoppingList) => shoppingList.id
        );

        const operation = this.shoppingListService.addBulkItemsToShoppingList({
            productVariantsIds,
            shoppingListIds,
        });

        operation.pipe(finalize(() => this.cdr.markForCheck())).subscribe({
            next: (result) => {
                if (
                    !result ||
                    result.__typename !== 'AddBulkItemsToShoppingListSuccess' ||
                    !result.success
                ) {
                    this.translateService
                        .get('shopping-list.product-list.assign-error')
                        .subscribe((message) => {
                            this.notificationService.error(
                                result?.message || message
                            );
                        });
                    return;
                }

                this.translateService
                    .get('shopping-list.product-list.assign-success')
                    .subscribe((message) => {
                        this.notificationService.success(message);
                    });

                this.resolveWith(true);
            },
            error: (error) => {
                console.error('error: ', error);
                this.translateService
                    .get('shopping-list.product-list.assign-error')
                    .subscribe((message) => {
                        this.notificationService.error(message);
                    });
            },
        });
    }

    cancel() {
        this.resolveWith();
    }

    setPageNumber(page: number): void {
        const itemsPerPage = this.itemsPerPage$.getValue();
        const skip = (page - 1) * itemsPerPage;

        this.currentPage$.next(page);
        this.loadShoppingLists(skip, itemsPerPage);
    }

    setItemsPerPage(items: number): void {
        this.itemsPerPage$.next(items);
        this.currentPage$.next(1);
        this.loadShoppingLists(0, items);
    }
}
